import React from 'react';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { classNames } from 'primereact/utils';
import { Checkbox } from 'primereact/checkbox';
import { RadioButton } from 'primereact/radiobutton';
import { CustomValidationMessage } from '../../../Components/CustomMessage';
import { createCustomHandleChangeApostrophe, createDescriptionDotsHandleChange } from '../../../Components/CleanInputs';
import { canShowSetupFee } from '../../../constants/planWizardTypes';

export const toInputNumberValue = (value) => {
  if (value === '' || value === null || value === undefined) {
    return null;
  }
  return value;
};

export const PlanTitle = ({ t, tierExist }) => (
  <span className="plan-title">
    {t('plan.plan_for')}{' '}
    {!tierExist ? 'General' : tierExist?.name}
  </span>
);

export const NameDescriptionFields = ({ formik, t, isFormFieldValid, getFormErrorMessage, errors, setErrors }) => {
  const customHandleChange = createCustomHandleChangeApostrophe(formik, setErrors, t);
  const customDescriptionDotsHandleChange = createDescriptionDotsHandleChange(formik, setErrors, t);

  const handleNameChange = (e) => {
    customHandleChange(e);
    if (formik.errors.name) {
      formik.setFieldError('name', undefined);
    }
  };

  return (
    <>
      <div className="p-field plan-wizard-field-section">
        <label htmlFor="name" className={classNames({ 'p-error': isFormFieldValid('name') })}>
          <div style={{ margin: '1rem 0rem 0.5rem 0rem' }}>
            <span className="labels">{t('plan.name')}</span>
            <span className="isterik">*</span>
          </div>
        </label>
        <InputText
          id="name"
          name="name"
          placeholder={t('plan.name')}
          value={formik.values.name}
          onChange={handleNameChange}
          onBlur={formik.handleBlur}
          className={classNames({ 'p-invalid': isFormFieldValid('name') })}
          maxLength={50}
        />
        <div className="plan-wizard-field-error">
          {getFormErrorMessage('name')}
        </div>
        {errors.name && <p className="character_errors">{errors.name}</p>}
      </div>
      <br />
      <div className="planCreate_description">
        <label htmlFor="description" className={classNames('labels_error_message', { 'p-error': isFormFieldValid('description') })}>
          <div className="labels_error_message">
            <span className="labels">{t('plan.plan_des')}</span>
          </div>
        </label>
        <InputText
          id="description"
          name="description"
          placeholder={t('plan.plan_des')}
          onChange={customDescriptionDotsHandleChange}
          onBlur={formik.handleBlur}
          value={formik.values.description}
          maxLength={100}
        />
        {errors.description && <p className="character_errors">{errors.description}</p>}
      </div>
    </>
  );
};

export const ProductSelectField = ({
  formik,
  t,
  planGroups,
  isFormFieldValid,
  getFormErrorMessagePlanId,
  onChangePlanGroup,
}) => (
  <div className="p-field plan-wizard-field-section" style={{ marginTop: '1rem' }}>
    <label htmlFor="planId" className={classNames({ 'p-error': isFormFieldValid('planId') })}>
      <span className="labels">{t('plan.product_name')}</span>
      <span className="isterik">*</span>
    </label>
    <Dropdown
      id="planId"
      name="planId"
      value={formik.values.planId}
      options={planGroups}
      optionLabel="name"
      onChange={onChangePlanGroup}
      placeholder={t('plan.select_title')}
      className={classNames({ 'p-invalid': isFormFieldValid('planId') })}
    />
    <div className="plan-wizard-field-error">
      {getFormErrorMessagePlanId('planId')}
    </div>
  </div>
);

export const CostField = ({
  formik,
  t,
  Trans,
  business,
  amountCurrency,
  isFormFieldValidCost,
  getFormErrorMessageCost,
  labelKey = 'plan.cost',
  required = true,
}) => (
  <div className="plan_cost_div">
    <div>
      <label htmlFor="cost" className={classNames({ 'p-error': isFormFieldValidCost('cost') })}>
        <div className="labels_error_message">
          <span className="labels">{t(labelKey)}</span>
          <span className="labels">({business?.currency})</span>
          {required && <span className="isterik">*</span>}
        </div>
      </label>
      <div className="inputFields">
        <InputNumber
          id="plan_cost"
          name="cost"
          maxFractionDigits={2}
          value={toInputNumberValue(formik.values.cost)}
          className={classNames({ 'p-invalid': isFormFieldValidCost('cost') })}
          onKeyDown={(e) => {
            const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', '.'];
            if (!allowedKeys.includes(e.key) && !/^\d$/.test(e.key)) {
              e.preventDefault();
            }
          }}
          onChange={(e) => {
            const value = e.value;
            if (value && value > 99999) {
              formik.setFieldError('cost', 'invitation.subscription_amount_range');
              return;
            }
            formik.setFieldValue('cost', value ?? null);
            formik.setFieldTouched('cost', true, false);
            if (formik.errors.cost && typeof formik.errors.cost === 'string') {
              formik.setFieldError('cost', undefined);
            }
            if (value !== null && value !== undefined) {
              if (value < 1 || value > 10000) {
                formik.setFieldError('cost', 'invitation.subscription_amount_range');
              } else {
                formik.setFieldError('cost', null);
              }
            } else {
              formik.setFieldError('cost', null);
            }
          }}
          onWheel={(e) => e?.target?.blur()}
          onBlur={(e) => {
            formik.handleBlur(e);
            formik.validateField('cost');
          }}
          placeholder={t('plan.plan_amount')}
          max={99999}
        />
      </div>
    </div>
    {formik.errors.cost !== 'invitation.subscription_amount_range' && (
      <span className="cost_error_message">{getFormErrorMessageCost('cost')}</span>
    )}
    {formik.touched.cost && formik.errors.cost === 'invitation.subscription_amount_range' && (
      <span className="cost_error_message">
        <CustomValidationMessage
          severity="error"
          text={(
            <Trans
              i18nKey="invitation.subscription_amount_range"
              values={{ currency: amountCurrency, minAmount: '1.00', maxAmount: '10,000.00' }}
              components={{
                1: <span className="trans_error_detail" />,
                2: <span className="trans_error_detail" />,
                3: <span className="trans_error_detail" />,
                4: <span className="trans_error_detail" />,
                5: <span className="trans_error_detail" />,
                6: <span className="trans_error_detail" />,
              }}
            />
          )}
        />
      </span>
    )}
  </div>
);

export const FixedFlexibleRadios = ({ formik, t, onSelectFixed, onSelectFlexible }) => (
  <div className="plan-wizard-field-section">
    <div className="plan-wizard-radio-group">
      <div className="plan-wizard-radio-option plan-wizard-radio-option-stacked">
        <RadioButton
          inputId="amount-fixed"
          className="custom-radio planCreate-radio"
          checked={formik.values.policy?.code === 'FIXED'}
          onChange={onSelectFixed}
        />
        <div>
          <label htmlFor="amount-fixed" className="plan-wizard-radio-label labels">{t('plan.wizard.amount_mode_fixed')}</label>
          <p className="light_hints">{t('plan.fixed_hint')}</p>
        </div>
      </div>
      <div className="plan-wizard-radio-option plan-wizard-radio-option-stacked">
        <RadioButton
          inputId="amount-flexible"
          className="custom-radio planCreate-radio"
          checked={formik.values.policy?.code === 'FLEXIBLE'}
          onChange={onSelectFlexible}
        />
        <div>
          <label htmlFor="amount-flexible" className="plan-wizard-radio-label labels">{t('plan.wizard.amount_mode_payer_chooses')}</label>
          <p className="light_hints">{t('plan.flexible_first_hint')}</p>
        </div>
      </div>
    </div>
  </div>
);

export const AutoBillingField = ({ formik, t, supportsAutoBilling, policyCode }) => {
  if (!supportsAutoBilling || !['FIXED', 'CUSTOM'].includes(policyCode)) {
    return null;
  }

  return (
    <div className="auto_billing_checkbox">
      <Checkbox
        inputId="autoBilling"
        name="autoBilling"
        onChange={(e) => {
          formik.setFieldValue('autoBilling', e.checked);
          if (e.checked) {
            formik.setFieldValue('feeCheck', false);
            formik.setFieldValue('setup', undefined);
          }
        }}
        checked={formik.values.autoBilling}
      />
      <div>
        <label htmlFor="autoBilling" className="auto_billing_label">
          {t('plan.auto_billing_label')}
        </label>
        <p className="auto_billing_hint">{t('plan.auto_billing_hint')}</p>
      </div>
    </div>
  );
};

export const SetupFeeFields = ({
  formik,
  t,
  business,
  wizardType,
  onChangeFeeCheck,
  isFormFieldValidSetupFee,
  getFormErrorMessageSetupFee,
  showForNonFixed = false,
}) => {
  const policyCode = formik.values.policy?.code;

  if (!canShowSetupFee(wizardType, formik.values)) {
    return null;
  }

  const isFixedSetup = policyCode === 'FIXED' && formik.values.feeCheck;
  const isNonFixedSetup = showForNonFixed && policyCode !== 'FIXED' && formik.values.feeCheck;

  return (
    <div className="plan-wizard-field-section">
      <div className="setup_checkbox">
        <Checkbox
          inputId="feeCheck"
          onChange={onChangeFeeCheck}
          checked={formik.values.feeCheck}
        />
        <label htmlFor="feeCheck" className="setup_fee_checkbox_label">
          {t('plan.fee_checkbox_label')}
        </label>
      </div>
      {(isFixedSetup || isNonFixedSetup) && (
        <div className="plan-wizard-setup-fields">
          <label htmlFor="setup" className={classNames({ 'p-error': isFormFieldValidSetupFee('setup') })}>
            <span className="labels">{t('plan.setup')}</span>
            <span className="labels"> ({business?.currency})</span>
            <span className="isterik">*</span>
          </label>
          <div className="inputFields plan-wizard-input">
            <InputNumber
              id="setup"
              name="setup"
              value={toInputNumberValue(formik.values.setup)}
              maxFractionDigits={2}
              placeholder={t('plan.setup')}
              className={classNames({ 'p-invalid': isFormFieldValidSetupFee('setup') })}
              onChange={(e) => {
                formik.setFieldValue('setup', e.value ?? null);
                if (formik.errors.setup) {
                  formik.setFieldError('setup', undefined);
                }
              }}
              onBlur={formik.handleBlur}
            />
          </div>
          <p className="plan-wizard-setup-hint">{t('plan.setup_included_first_bill')}</p>
          <div className="plan-wizard-field-error">
            {getFormErrorMessageSetupFee('setup')}
          </div>
        </div>
      )}
    </div>
  );
};

const WizardRadioOption = ({ id, checked, onChange, label }) => (
  <div className="plan-wizard-radio-option">
    <RadioButton
      inputId={id}
      className="custom-radio planCreate-radio"
      onChange={onChange}
      checked={checked}
    />
    <label htmlFor={id} className="plan-wizard-radio-label">{label}</label>
  </div>
);

export const RecoverMissedFields = ({
  t,
  recoverMissed,
  notRecover,
  onChangeRecover,
  onChangeNotRecover,
  idPrefix = 'plan',
}) => (
  <div className="plan-wizard-field-section">
    <label htmlFor={`${idPrefix}-recover-no`}>
      <span className="labels">{t('plan.missed_payment_recover')}</span>
      <span className="isterik">*</span>
    </label>
    <div className="plan-wizard-radio-group">
      <WizardRadioOption
        id={`${idPrefix}-recover-no`}
        checked={notRecover}
        onChange={onChangeNotRecover}
        label={t('plan.recover_false')}
      />
      <WizardRadioOption
        id={`${idPrefix}-recover-yes`}
        checked={recoverMissed}
        onChange={onChangeRecover}
        label={t('plan.recover_true')}
      />
    </div>
  </div>
);

export const TargetPartsFields = ({
  t,
  equalParts,
  varying,
  onChangeEqualParts,
  onChangeVarying,
  recoverMissed,
  notRecover,
  onChangeRecover,
  onChangeNotRecover,
}) => (
  <div className="plan-wizard-target-fields">
    <div className="plan-wizard-field-section">
      <label htmlFor="target-parts-equal">
        <span className="labels">{t('plan.parts_heading')}</span>
        <span className="isterik">*</span>
      </label>
      <div className="plan-wizard-radio-group">
        <WizardRadioOption
          id="target-parts-equal"
          checked={equalParts}
          onChange={onChangeEqualParts}
          label={t('plan.parts_equal')}
        />
        <WizardRadioOption
          id="target-parts-vary"
          checked={varying}
          onChange={onChangeVarying}
          label={t('plan.parts_vary')}
        />
      </div>
    </div>
    {equalParts && (
      <RecoverMissedFields
        t={t}
        recoverMissed={recoverMissed}
        notRecover={notRecover}
        onChangeRecover={onChangeRecover}
        onChangeNotRecover={onChangeNotRecover}
        idPrefix="target"
      />
    )}
  </div>
);
